import { transfer } from 'comlink';
import { AbstractVideoEngine } from './AbstractVideoEngine';
import { SafariVideoDecoder } from './SafariVideoDecoder';
/**
 * Video engine for browsers without main-thread `MediaStreamTrackProcessor`
 * (Safari, Firefox). Video frame extraction runs inside the unified
 * `VideoWorker` so `VideoFrame` handles never cross worker boundaries — a
 * separate extractor worker would have to ship `VideoFrame`s through a
 * `MessagePort` which on WebKit routes via the main thread and can stall
 * under load.
 */
export class StandardVideoEngine extends AbstractVideoEngine {
    LOG_PREFIX = '[StandardVideoEngine]';
    safariVideoDecoder;
    // TODO: temporarily disabled SafariVideoDecoder — revert to re-enable
    useWebRTCDecoder = false; // 'RTCRtpScriptTransform' in globalThis;
    constructor() {
        super();
        console.log(`${this.LOG_PREFIX} constructor called`);
        if (this.useWebRTCDecoder) {
            console.log(`${this.LOG_PREFIX} RTCRtpScriptTransform available, will use WebRTC decoder path`);
        }
    }
    // ========== Safari WebRTC decoder overrides ==========
    async startVideoDecoderWorker() {
        if (!this.useWebRTCDecoder) {
            return super.startVideoDecoderWorker();
        }
        if (this.decoderStarted) {
            console.warn(`${this.LOG_PREFIX} videoDecoder already running`);
            return;
        }
        if (!this.playerStarted) {
            throw new Error('player must be running before starting decoder');
        }
        console.log(`${this.LOG_PREFIX} startVideoDecoderWorker (Safari WebRTC path)`);
        this.safariVideoDecoder = new SafariVideoDecoder();
        await this.safariVideoDecoder.init();
        // TODO: SafariVideoDecoder still talks via a MessagePort — adapt
        // it to feed decoded tracks directly into the unified VideoWorker
        // when this path is re-enabled.
        this.decoderStarted = true;
        console.log(`${this.LOG_PREFIX} videoDecoder started (Safari WebRTC path)`);
    }
    async stopVideoDecoderWorker() {
        if (!this.safariVideoDecoder) {
            return super.stopVideoDecoderWorker();
        }
        console.log(`${this.LOG_PREFIX} stopVideoDecoderWorker (Safari WebRTC path)`);
        try {
            await this.safariVideoDecoder.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} SafariVideoDecoder destroy failed`, error);
        }
        this.safariVideoDecoder = undefined;
        this.decoderStarted = false;
        console.log(`${this.LOG_PREFIX} videoDecoder stopped (Safari WebRTC path)`);
    }
    /**
     * The worker extracts frames from a MediaStreamTrack it owns. A
     * transferred track is ended in the realm it leaves (WebKit does this
     * silently, no 'ended' event), so transferring the page's track would
     * leave MediaService holding a dead camera: the next media re-sync
     * finds no live source track and removes the camera that is in fact
     * still encoding (seen on Safari at a viewer-quality switch). The
     * worker gets a clone and publishes it under the page's track id; the
     * page's track stays live for the device watchdog and later re-syncs.
     */
    async addMediaStreamVideoTrack(track) {
        const clone = track.clone();
        console.log(`${this.LOG_PREFIX} addMediaStreamVideoTrack called`, {
            trackId: track.id,
            cloneId: clone.id,
        });
        await this.videoWorkerProxy?.addMediaStreamVideoTrack(transfer(clone, [clone]), track.id);
    }
    async removeMediaStreamVideoTrack(track) {
        console.log(`${this.LOG_PREFIX} removeMediaStreamVideoTrack called`, {
            trackId: track.id,
        });
        await this.videoWorkerProxy?.removeMediaStreamVideoTrack(track.id);
        console.log(`${this.LOG_PREFIX} removeMediaStreamVideoTrack completed`, { trackId: track.id });
    }
    async setScreenOrientation(type, angle) {
        await super.setScreenOrientation(type, angle);
        await this.videoWorkerProxy?.setScreenOrientation(type, angle);
    }
}
